import React from 'react';
import {
    BodyTextSmall,
    CalloutInline,
    Button,
    ButtonGroup,
    HeadingText30,
    Modal,
} from '@modernhealth/vital-components';
import { Trans, useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { colors } from '@modernhealth/vital-foundation';

import { FormattedSessionDateTimeRange } from 'src/modules/videoSessions/DateTimeFormatter';

import { SessionSlot } from '../models/videoSessions';

const ModalContainer = styled('div')({
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'space-between',
    padding: 24,
});

const ButtonsContainer = styled('div')({ width: '100%', marginTop: 16 });

const BlueHourSpan = styled('span')({ fontWeight: 'bold', color: colors['blue55'] });

interface Props {
    isVisible: boolean;
    isLoading?: boolean;
    sessionSlot: SessionSlot;
    onClose: () => void;
    onPrimaryPress: () => void;
    isPamm?: boolean;
}

export const ScheduleHealthPlanSessionConfirmationModal: React.FC<Props> = ({
    isVisible,
    isLoading,
    sessionSlot,
    onClose,
    onPrimaryPress,
    isPamm,
}) => {
    const { t } = useTranslation();

    return (
        <Modal isVisible={isVisible} onCloseButtonClick={onClose} maxWidth={576} noPaddings>
            <ModalContainer>
                <HeadingText30 marginBottom={16}>
                    <FormattedSessionDateTimeRange
                        start={sessionSlot.start.toDate()}
                        end={sessionSlot.end.toDate()}
                    />
                </HeadingText30>
                {!isPamm && (
                    <CalloutInline variant="info">
                        {t('videoSessions:hpi_session_confirmation_modal.callout')}
                    </CalloutInline>
                )}
                {isPamm && (
                    <BodyTextSmall>
                        {t('videoSessions:components.pamm_session_modal.text.body.part1')}
                    </BodyTextSmall>
                )}
                <BodyTextSmall marginTop={16}>
                    <Trans
                        t={t}
                        i18nKey={
                            isPamm
                                ? 'videoSessions:components.pamm_session_modal.text.body.part2'
                                : 'videoSessions:hpi_session_confirmation_modal.body'
                        }
                        values={{ hours: 48 }}
                        components={{
                            blue: <BlueHourSpan />,
                        }}
                    />
                </BodyTextSmall>
                <ButtonsContainer>
                    <ButtonGroup>
                        <Button
                            size="medium"
                            busy={isLoading}
                            onClick={onClose}
                            variant="secondary"
                        >
                            {isPamm ? t('common.back') : t('payments:back_schedule')}
                        </Button>
                        <Button
                            size="medium"
                            disabled={isLoading}
                            onClick={onPrimaryPress}
                            variant="primary"
                        >
                            {isPamm
                                ? t('common.continue')
                                : t('videoSessions:hpi_session_confirmation_modal.review_session')}
                        </Button>
                    </ButtonGroup>
                </ButtonsContainer>
            </ModalContainer>
        </Modal>
    );
};
